import { CoreApiClient } from '../../core/client'
import type { DataScopeGrant } from '../../core/types'

/** 租户基础信息 */
export interface Tenant {
  id: number
  code: string
  name: string
  status: 'ACTIVE' | 'SUSPENDED'
  domain: string
  tenantMode: 'COLUMN' | 'DATASOURCE'
  packageCode: string
  packageName: string
  loginAccount: string
  remark: string
}

/** 租户已开通模块 */
export interface TenantModule {
  tenantId: number
  moduleCode: string
  enabled: boolean
  expiresAt?: string | null
}

/** 创建租户请求 */
export interface CreateTenantRequest {
  code: string
  name: string
  domain: string
  tenantMode: 'COLUMN' | 'DATASOURCE'
  packageCode: string
  loginAccount: string
  loginPassword: string
  remark: string
  status: 'ACTIVE' | 'SUSPENDED'
}

/** 租户模块授权请求 */
export interface GrantModuleRequest {
  moduleCode: string
  expiresAt?: string | null
}

/** 更新租户请求 */
export interface UpdateTenantRequest {
  name: string
  domain: string
  tenantMode: 'COLUMN' | 'DATASOURCE'
  packageCode: string
  loginAccount: string
  loginPassword: string
  remark: string
  status: 'ACTIVE' | 'SUSPENDED'
}

/** 平台全局菜单 */
export interface PlatformMenu {
  code: string
  name: string
  path: string
  permission: string
  enabled: boolean
  sortOrder: number
}

/** 平台全局角色 */
export interface PlatformRole {
  id: number
  code: string
  name: string
  menuCodes: string[]
  permissions: string[]
}

/** 平台全局用户 */
export interface PlatformUser {
  id: number
  username: string
  displayName: string
  roleIds: number[]
  enabled: boolean
}

/** 组织部门 */
export interface Dept {
  id: number
  tenantId: number
  parentId?: number | null
  code: string
  name: string
  sortOrder: number
  enabled: boolean
}

export interface CreateDeptRequest {
  parentId?: number | null
  code: string
  name: string
  sortOrder: number
}

export interface UpdateDeptRequest {
  parentId?: number | null
  name: string
  sortOrder: number
  enabled: boolean
}

/** 系统参数 */
export interface SystemParam {
  tenantId: number
  code: string
  value: string
  remark: string
  createdAt: string
  updatedAt: string
}

export interface CreateParamRequest {
  code: string
  value: string
  remark: string
}

export interface UpdateParamRequest {
  value: string
  remark: string
}

/** 字典类型 */
export interface DictType {
  tenantId: number
  name: string
  type: string
  sortOrder: number
  remark: string
  createdAt: string
  updatedAt: string
}

export interface CreateDictTypeRequest {
  name: string
  type: string
  sortOrder: number
  remark: string
}

export interface UpdateDictTypeRequest {
  name: string
  sortOrder: number
  remark: string
}

/** 字典项 */
export interface DictItem {
  tenantId: number
  dictType: string
  label: string
  value: string
  sortOrder: number
  remark: string
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateDictItemRequest {
  label: string
  value: string
  sortOrder: number
  remark: string
  enabled: boolean
}

export interface UpdateDictItemRequest {
  label: string
  sortOrder: number
  remark: string
  enabled: boolean
}

/** 开放平台开发者 */
export interface OpenDeveloper {
  id: number
  name: string
  developerType: string
  contactName: string
  contactEmail: string
  status: string
}

export interface CreateOpenDeveloperRequest {
  name: string
  developerType: string
  contactName: string
  contactEmail: string
}

/** 开放应用 */
export interface OpenApp {
  id: number
  developerId?: number
  developerName?: string
  appName: string
  appType: string
  clientId: string
  clientSecret?: string
  redirectUris?: string[]
  ipWhitelist?: string[]
  status: string
  createdAt: string
}

export interface CreateOpenAppRequest {
  developerId: number
  appName: string
  appType: string
  redirectUris: string[]
  ipWhitelist: string[]
}

export interface OpenApiResource {
  moduleCode: string
  moduleName: string
  resourceCode: string
  method: string
  path: string
  permission: string
  dataResource?: string | null
  rateLimitPolicy: string
}

export interface TenantOpenAppGrant {
  tenantId: number
  appId: number
  moduleCodes: string[]
  permissions: string[]
  dataScopes: DataScopeGrant[]
  status: 'ACTIVE' | 'SUSPENDED' | 'REVOKED' | string
  expiresAt?: string | null
}

export interface UpdateTenantOpenAppGrantRequest {
  moduleCodes: string[]
  permissions: string[]
  dataScopes: DataScopeGrant[]
  status: 'ACTIVE' | 'SUSPENDED' | 'REVOKED' | string
  expiresAt?: string | null
}

export interface CreatePlatformRoleRequest {
  code: string
  name: string
  menuCodes: string[]
  permissions: string[]
}

export interface CreatePlatformUserRequest {
  username: string
  displayName: string
  roleIds: number[]
}

export interface UpdatePlatformMenuRequest {
  name: string
  enabled: boolean
  sortOrder: number
}

/** 模块元数据注册 */
export interface ModuleMetadata {
  moduleId: string
  moduleCode: string
  moduleName: string
  moduleType: 'ADMIN' | 'SAAS' | 'CLIENT' | string
  serviceName: string
  version: string
  serviceEntries: ModuleServiceEntry[]
  menus: MenuResource[]
  permissions: string[]
  apis: ApiResource[]
  dataResources: DataResource[]
  capabilities: ModuleCapability[]
}

export interface ModuleServiceEntry {
  moduleId: string
  moduleType: 'ADMIN' | 'SAAS' | 'CLIENT' | string
  serviceName: string
  apiPrefix: string
  manifestPath: string
}

export interface MenuResource {
  code: string
  parentCode?: string | null
  name: string
  icon: string
  menuType: 'MENU' | 'BUTTON' | 'IFRAME' | 'LINK' | string
  openMode: 'INTERNAL' | 'EXTERNAL' | 'IFRAME' | 'NEW_TAB' | string
  path: string
  permission: string
  remoteEntry: string
  exposedModule: string
  hidden: boolean
}

export interface TenantMenu {
  tenantId: number
  moduleCode: string
  menuCode: string
  parentCode?: string | null
  name: string
  icon: string
  menuType: 'MENU' | 'BUTTON' | 'IFRAME' | 'LINK' | string
  openMode: 'INTERNAL' | 'EXTERNAL' | 'IFRAME' | 'NEW_TAB' | string
  path: string
  permission: string
  remoteEntry: string
  exposedModule: string
  enabled: boolean
  hidden: boolean
  sortOrder: number
}

export interface ApiResource {
  method: string
  path: string
  permission: string
}

export interface DataResource {
  code: string
  tableName: string
  tenantColumn: string
  ownerColumn: string
  deptColumn: string
}

export interface ModuleCapability {
  code: string
  name: string
  description: string
  capabilityType: 'FEATURE' | 'CONFIG' | 'WORKBENCH' | 'DECORATION' | 'WORKFLOW' | 'CLIENT_API' | 'OPEN_API' | 'DATA' | string
  permission: string
  entryModuleType: 'ADMIN' | 'SAAS' | 'CLIENT' | string
}

export interface CreateMenuRequest extends UpdateMenuRequest {
  moduleCode: string
  menuCode: string
}

export interface UpdateMenuRequest {
  parentCode?: string | null
  name: string
  icon: string
  menuType: 'MENU' | 'BUTTON' | 'IFRAME' | 'LINK' | string
  openMode: 'INTERNAL' | 'EXTERNAL' | 'IFRAME' | 'NEW_TAB' | string
  path: string
  permission: string
  remoteEntry: string
  exposedModule: string
  enabled: boolean
  hidden: boolean
  sortOrder: number
}

export interface SyncModuleRequest {
  manifestUrl: string
}

/** AI 智能体请求/响应 */
export interface AgentChatRequest {
  message: string
  context?: Record<string, unknown>
}

export interface AgentChatAction {
  code: string
  name: string
  status: string
  description: string
  arguments: Record<string, unknown>
  result: unknown
}

export interface AgentChatResponse {
  conversationId: string
  status: string
  intent: string
  toolCode: string
  answer: string
  data: unknown
  actions: AgentChatAction[]
  followUps: string[]
  context: Record<string, unknown>
}

export interface AgentModelConfig {
  enabled: boolean
  state: 'READY' | 'DISABLED' | 'NOT_CONFIGURED'
  providerLabel: string
  modelLabel: string
  hasBaseUrl: boolean
  hasApiKey: boolean
  temperature: number
  timeoutSeconds: number
  revision: string
}

export interface AgentModelStatus {
  enabled: boolean
  provider: string
  model: string
  hasApiKey: boolean
  mode: string
}

export interface AgentModelConfigRequest {
  enabled: boolean
  provider: string
  baseUrl: string
  model: string
  apiKey?: string
  temperature: number
  timeoutSeconds: number
}

export interface AgentModelTestResult {
  success: boolean
  mode: string
  message: string
  provider: string
  model: string
  response: string
}

export interface AgentToolParameter {
  name: string
  type: string
  required: boolean
  description: string
  defaultValue: unknown
}

export interface AgentTool {
  code: string
  name: string
  description: string
  moduleCode: string
  moduleName: string
  serviceName: string
  toolType: 'QUERY' | 'ACTION' | 'CONFIG' | string
  permission: string
  dataScope: string
  parameters: AgentToolParameter[]
  metadata: Record<string, unknown>
}

export interface AgentToolInvokeRequest {
  arguments?: Record<string, unknown>
}

export interface AgentToolInvokeResult {
  toolCode: string
  summary: string
  data: unknown
  context: Record<string, unknown>
  auditNotes: string[]
}

/** 认证与会话契约 */
export interface LoginResponse {
  tenantId: number
  userId: number
  deptId: number
  username: string
  tenantDomain?: string
  token: string
  permissions: string[]
  dataScopes: DataScopeGrant[]
  modules: string[]
  menuCodes?: string[]
}

export interface PlatformLoginResponse {
  userId: number
  username: string
  displayName: string
  token: string
  permissions: string[]
  menus: PlatformMenu[]
}

export interface Role {
  id: number
  tenantId: number
  code: string
  name: string
  menuCodes: string[]
  permissions: string[]
  dataScopes: DataScopeGrant[]
}

export interface CreateRoleRequest {
  code: string
  name: string
  menuCodes: string[]
  permissions: string[]
  dataScopes: DataScopeGrant[]
}

export interface UpdateRoleRequest {
  name: string
  menuCodes: string[]
  permissions: string[]
  dataScopes: DataScopeGrant[]
}

export interface User {
  id: number
  tenantId: number
  username: string
  displayName: string
  deptId: number
  roleIds: number[]
  enabled: boolean
}

export interface CreateUserRequest {
  username: string
  displayName: string
  deptId: number
  roleIds: number[]
}

export interface ClientMenu {
  tenantId: number
  moduleCode: string
  menuCode: string
  parentCode?: string | null
  name: string
  icon: string
  path: string
  permissionCode: string
  enabled: boolean
  sortOrder: number
}

export interface ClientRole {
  id: number
  tenantId: number
  moduleCode: string
  code: string
  name: string
  menuCodes: string[]
  permissions: string[]
}

export interface ClientUser {
  id: number
  tenantId: number
  username: string
  displayName: string
  mobile: string
  enabled: boolean
  roleIds: number[]
  shopIds: number[]
  createdAt: string
  updatedAt: string
}

export interface ClientRbacCatalog {
  menus: ClientMenu[]
  permissions: string[]
}

export interface CreateClientRoleRequest {
  moduleCode: string
  code: string
  name: string
  menuCodes: string[]
  permissions: string[]
}

export interface UpdateClientRoleRequest {
  name: string
  menuCodes: string[]
  permissions: string[]
}

export interface CreateClientUserRequest {
  username: string
  displayName: string
  mobile: string
  password: string
  enabled: boolean
  roleIds: number[]
  shopIds: number[]
}

export interface UpdateClientUserRequest {
  displayName: string
  mobile: string
  password?: string
  enabled: boolean
  roleIds: number[]
  shopIds: number[]
}

export type ModuleUserStatus = 'ACTIVE' | 'SUSPENDED'

export interface ModuleUser {
  id: number
  tenantId: number
  tenantCode?: string | null
  tenantName?: string | null
  moduleCode: string
  moduleName?: string | null
  username: string
  nickname: string
  mobile?: string | null
  email?: string | null
  externalId?: string | null
  status: ModuleUserStatus
  source: string
  createdAt: string
  updatedAt: string
}

export interface CreateModuleUserRequest {
  tenantId?: number | null
  moduleCode: string
  username: string
  nickname: string
  mobile?: string | null
  email?: string | null
  externalId?: string | null
  source?: string | null
}

export interface UpdateModuleUserRequest {
  nickname: string
  mobile?: string | null
  email?: string | null
  externalId?: string | null
  source?: string | null
}

export interface UpdateModuleUserStatusRequest {
  status: ModuleUserStatus
}

export interface CurrentPermissionView {
  permissions: string[]
  apis: ApiResource[]
  dataResources: DataResource[]
}

export const SYSTEM_PERMISSIONS = {
  workspaceAccess: 'system:workspace:access',
  workspaceAnalyticsView: 'system:workspace:analytics:view',
  agentChat: 'system:agent:chat',
  permissionView: 'system:permission:view',
  menuRead: 'system:menu:read',
  menuCreate: 'system:menu:create',
  menuUpdate: 'system:menu:update',
  menuDelete: 'system:menu:delete',
  rbacRead: 'system:rbac:read',
  roleCreate: 'system:role:create',
  roleUpdate: 'system:role:update',
  userCreate: 'system:user:create',
  deptRead: 'system:dept:read',
  deptCreate: 'system:dept:create',
  deptUpdate: 'system:dept:update',
  paramRead: 'system:param:read',
  paramCreate: 'system:param:create',
  paramUpdate: 'system:param:update',
  paramDelete: 'system:param:delete',
  dictRead: 'system:dict:read',
  dictCreate: 'system:dict:create',
  dictUpdate: 'system:dict:update',
  dictDelete: 'system:dict:delete',
  dictItemRead: 'system:dict-item:read',
  dictItemCreate: 'system:dict-item:create',
  dictItemUpdate: 'system:dict-item:update',
  dictItemDelete: 'system:dict-item:delete',
  moduleRead: 'system:module:read',
  moduleSync: 'system:module:sync',
  moduleUserRead: 'system:module-user:read',
  moduleUserCreate: 'system:module-user:create',
  moduleUserUpdate: 'system:module-user:update',
  clientRbacRead: 'system:client-rbac:read',
  clientRoleCreate: 'system:client-role:create',
  clientRoleUpdate: 'system:client-role:update',
  clientUserCreate: 'system:client-user:create',
  clientUserUpdate: 'system:client-user:update',
  notificationRead: 'system:notification:read',
  notificationManage: 'system:notification:manage',
  openAppRead: 'system:open-app:read',
  openAppGrant: 'system:open-app:grant'
} as const

export const SYSTEM_PERMISSION_LIST = Object.values(SYSTEM_PERMISSIONS)

/** 系统与平台 API 模块 */
export class SystemApiModule {
  private readonly client: CoreApiClient

  constructor(client: CoreApiClient) {
    this.client = client
  }

  chatPlatformAgent(body: AgentChatRequest) {
    return this.client.post<AgentChatResponse>('/api/admin/agent/chat', body)
  }

  getPlatformAgentModelConfig() {
    return this.client.get<AgentModelConfig>('/api/admin/agent/model')
  }

  savePlatformAgentModelConfig(body: AgentModelConfigRequest) {
    return this.client.put<AgentModelConfig>('/api/admin/agent/model', body)
  }

  testPlatformAgentModel(message = '') {
    return this.client.post<AgentModelTestResult>('/api/admin/agent/model/test', { message })
  }

  getPlatformAgentModelStatus() {
    return this.client.get<AgentModelStatus>('/api/admin/agent/model/status')
  }

  chatSaasAgent(body: AgentChatRequest) {
    return this.client.post<AgentChatResponse>('/api/saas/system/agent/chat', body)
  }

  getSaasAgentModelStatus() {
    return this.client.get<AgentModelStatus>('/api/saas/system/agent/model/status')
  }

  getAgentTools() {
    return this.client.get<AgentTool[]>('/api/agent/tools')
  }

  invokeAgentTool(toolCode: string, body: AgentToolInvokeRequest = {}) {
    return this.client.post<AgentToolInvokeResult>(`/api/agent/tools/${toolCode}/invoke`, body)
  }
}
